import type {
  ProviderPhoto,
  ProviderPhotoKey,
  ProviderPhotosResponse,
} from "@src/interfaces/provider-photo.interface";
import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import { toFiniteNumber } from "@src/utils/number-utils";

import {
  buildProviderPhotoKey,
  isSupportedProviderPhotoContentType,
} from "@src/utils/provider-photo-utils";
import ClientLogsService from "./client-logs.service";
import { buildProviderPhotosConfig } from "./configs";

export interface GetProviderPhotosRequest {
  providers: ProviderPhotoKey[];
  user_uuid?: string;
}

interface ProviderHubPhotosResponse {
  data?: unknown;
  success?: boolean;
  error?: unknown;
  errors?: unknown;
  message?: unknown;
}

type NormalizedProviderPhotosRequest =
  | {
      providers: ProviderPhotoKey[];
    }
  | {
      error: string;
    };

export const PROVIDER_PHOTO_MEMORY_CACHE_MAX_KEYS = 75;

const MAX_PROVIDERS_PER_REQUEST = 100;
const PROVIDER_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const providerPhotoMemoryCache = new Map<string, ProviderPhoto>();

export function clearProviderPhotoMemoryCache(): void {
  providerPhotoMemoryCache.clear();
}

function setCachedProviderPhoto(providerPhoto: ProviderPhoto): void {
  const providerPhotoKey = buildProviderPhotoKey(providerPhoto);

  providerPhotoMemoryCache.delete(providerPhotoKey);
  providerPhotoMemoryCache.set(providerPhotoKey, providerPhoto);

  while (providerPhotoMemoryCache.size > PROVIDER_PHOTO_MEMORY_CACHE_MAX_KEYS) {
    const oldestProviderPhotoKey = providerPhotoMemoryCache.keys().next().value;
    providerPhotoMemoryCache.delete(oldestProviderPhotoKey);
  }
}

export function getCachedProviderPhoto(providerKey: ProviderPhotoKey): ProviderPhoto | undefined {
  const providerPhotoKey = buildProviderPhotoKey(providerKey);
  const providerPhoto = providerPhotoMemoryCache.get(providerPhotoKey);

  if (providerPhoto) {
    providerPhotoMemoryCache.delete(providerPhotoKey);
    providerPhotoMemoryCache.set(providerPhotoKey, providerPhoto);
  }

  return providerPhoto;
}

export function getCachedProviderPhotosByProviderKey(
  providerKeys: ProviderPhotoKey[],
): Record<string, ProviderPhoto> {
  return providerKeys.reduce<Record<string, ProviderPhoto>>((photoMap, providerKey) => {
    const providerPhotoKey = buildProviderPhotoKey(providerKey);
    const providerPhoto = getCachedProviderPhoto(providerKey);

    if (providerPhoto) {
      photoMap[providerPhotoKey] = providerPhoto;
    }

    return photoMap;
  }, {});
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const normalizeContextId = (contextId: unknown): number | null => {
  if (typeof contextId === "number" && Number.isInteger(contextId) && contextId > 0) {
    return contextId;
  }

  if (typeof contextId === "string" && /^\d+$/.test(contextId.trim())) {
    const parsedContextId = Number(contextId);
    return parsedContextId > 0 ? parsedContextId : null;
  }

  return null;
};

const normalizeProviderId = (providerId: unknown): string | null => {
  if (typeof providerId !== "string" && typeof providerId !== "number") {
    return null;
  }

  const normalizedProviderId = String(providerId).trim();
  if (
    normalizedProviderId.length === 0 ||
    normalizedProviderId.length > 64 ||
    !PROVIDER_ID_PATTERN.test(normalizedProviderId)
  ) {
    return null;
  }

  return normalizedProviderId;
};

const normalizeProviderRequestItem = (item: unknown, index: number): ProviderPhotoKey | string => {
  if (!isRecord(item)) {
    return `providers[${index}] must be an object with contextId and providerId`;
  }

  const contextId = normalizeContextId(item.contextId);
  if (contextId === null) {
    return `providers[${index}].contextId must be a positive integer`;
  }

  const providerId = normalizeProviderId(item.providerId);
  if (providerId === null) {
    return `providers[${index}].providerId must be a non-empty string containing only letters, numbers, underscores, or hyphens`;
  }

  return { contextId, providerId };
};

const normalizeProviderPhotosRequest = (providers: unknown): NormalizedProviderPhotosRequest => {
  if (!Array.isArray(providers)) {
    return { error: "providers must be an array" };
  }

  if (providers.length === 0) {
    return { providers: [] };
  }

  if (providers.length > MAX_PROVIDERS_PER_REQUEST) {
    return {
      error: `providers cannot include more than ${MAX_PROVIDERS_PER_REQUEST} providers`,
    };
  }

  const normalizedProviders: ProviderPhotoKey[] = [];
  for (let index = 0; index < providers.length; index += 1) {
    const normalizedProvider = normalizeProviderRequestItem(providers[index], index);
    if (typeof normalizedProvider === "string") {
      return { error: normalizedProvider };
    }

    normalizedProviders.push(normalizedProvider);
  }

  return {
    providers: normalizedProviders,
  };
};

const normalizeString = (value: unknown): string | undefined => {
  return typeof value === "string" ? value : undefined;
};

const normalizeNullableString = (value: unknown): string | null | undefined => {
  return value === null ? null : normalizeString(value);
};

const createNoPhotoResponse = (provider: ProviderPhotoKey): ProviderPhoto => {
  return {
    ...provider,
    hasPhoto: false,
  };
};

const normalizeProviderPhoto = (
  value: unknown,
  fallbackProvider?: ProviderPhotoKey,
): ProviderPhoto | null => {
  if (!isRecord(value)) {
    return null;
  }

  const contextId = normalizeContextId(value.contextId) ?? fallbackProvider?.contextId;
  const providerId = normalizeProviderId(value.providerId) ?? fallbackProvider?.providerId;

  if (!contextId || !providerId) {
    return null;
  }

  const photoContentType = normalizeString(value.photoContentType);
  const supportsPhotoContentType = isSupportedProviderPhotoContentType(photoContentType);
  const providerPhotoBase64 = supportsPhotoContentType
    ? normalizeString(value.providerPhotoBase64)
    : undefined;

  return {
    contextId,
    providerId,
    providerPhotoBase64,
    departmentName: normalizeString(value.departmentName),
    hasPhoto: value.hasPhoto === true && Boolean(providerPhotoBase64),
    photoAttachmentName: normalizeString(value.photoAttachmentName),
    photoContentType: supportsPhotoContentType ? photoContentType : undefined,
    photoSizeBytes: toFiniteNumber(value.photoSizeBytes),
    photoCreatedAt: normalizeNullableString(value.photoCreatedAt),
    photoModifiedAt: normalizeNullableString(value.photoModifiedAt),
  };
};

const getProviderHubErrorMessage = (
  statusCode: number,
  data: ProviderHubPhotosResponse,
): string => {
  if (normalizeString(data.error)) {
    return data.error as string;
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors.map(String).join(", ");
  }

  if (normalizeString(data.errors)) {
    return data.errors as string;
  }

  if (normalizeString(data.message)) {
    return data.message as string;
  }

  switch (statusCode) {
    case 400:
      return "Bad request - missing or invalid provider photo parameters";
    case 403:
      return "User has not completed the required steps to access this resource";
    case 404:
      return "Provider photos were not found";
    case 500:
      return "Internal server error occurred";
    default:
      return "An unknown error occurred, please try again later.";
  }
};

const getUpstreamPhotoList = (data: ProviderHubPhotosResponse): unknown[] => {
  if (Array.isArray(data.data)) {
    return data.data;
  }

  if (isRecord(data.data) && Array.isArray(data.data.photos)) {
    return data.data.photos;
  }

  return [];
};

export async function getProviderPhotos(
  request: GetProviderPhotosRequest,
): Promise<ProviderPhotosResponse> {
  const normalizedRequest = normalizeProviderPhotosRequest(request.providers);
  if ("error" in normalizedRequest) {
    return {
      success: false,
      error: normalizedRequest.error,
    };
  }

  if (normalizedRequest.providers.length === 0) {
    return {
      success: true,
      data: {
        photos: [],
      },
    };
  }

  const providersToFetch: ProviderPhotoKey[] = [];
  const providersToFetchKeys = new Set<string>();

  for (const provider of normalizedRequest.providers) {
    const providerPhotoKey = buildProviderPhotoKey(provider);

    if (
      !providerPhotoMemoryCache.has(providerPhotoKey) &&
      !providersToFetchKeys.has(providerPhotoKey)
    ) {
      providersToFetch.push(provider);
      providersToFetchKeys.add(providerPhotoKey);
    }
  }

  const buildResponse = (
    fetchedPhotosByProviderKey: Map<string, ProviderPhoto> = new Map(),
  ): ProviderPhotosResponse => {
    return {
      success: true,
      data: {
        photos: normalizedRequest.providers.map((provider) => {
          const providerKey = buildProviderPhotoKey(provider);

          return (
            fetchedPhotosByProviderKey.get(providerKey) ||
            getCachedProviderPhoto(provider) ||
            createNoPhotoResponse(provider)
          );
        }),
      },
    };
  };

  if (providersToFetch.length === 0) {
    return buildResponse();
  }

  try {
    const { data, response } = await callApi(
      buildProviderPhotosConfig({
        user_uuid: request.user_uuid,
        providers: providersToFetch,
      }),
    );
    const upstreamData = (data ?? {}) as ProviderHubPhotosResponse;

    if (!response.ok || upstreamData.success === false) {
      ClientLogsService.logError(
        request.user_uuid || "unknown",
        "provider-photos.service.getProviderPhotos",
        "Provider photos request failed",
        new Error(getProviderHubErrorMessage(response.status, upstreamData)),
        {
          errorType: response.ok
            ? "DependencyFailure"
            : getFailureTypeForHttpStatus(response.status),
          dependencyName: "ProviderPhotosAPI",
          functionName: "getProviderPhotos",
        },
      );
      return {
        success: false,
        error: getProviderHubErrorMessage(response.status, upstreamData),
      };
    }

    const providerHubPhotos = getUpstreamPhotoList(upstreamData);
    const fetchedPhotosByProviderKey = new Map<string, ProviderPhoto>();
    const requestedProviderKeys = new Set(
      providersToFetch.map((provider) => {
        return buildProviderPhotoKey(provider);
      }),
    );

    providerHubPhotos.forEach((photo) => {
      const normalizedPhoto = normalizeProviderPhoto(photo);
      if (!normalizedPhoto) {
        return;
      }

      const providerKey = buildProviderPhotoKey(normalizedPhoto);
      if (!requestedProviderKeys.has(providerKey)) {
        return;
      }

      fetchedPhotosByProviderKey.set(providerKey, normalizedPhoto);
      setCachedProviderPhoto(normalizedPhoto);
    });

    providersToFetch.forEach((provider) => {
      const providerKey = buildProviderPhotoKey(provider);
      if (!fetchedPhotosByProviderKey.has(providerKey)) {
        const noPhotoResponse = createNoPhotoResponse(provider);
        fetchedPhotosByProviderKey.set(providerKey, noPhotoResponse);
        setCachedProviderPhoto(noPhotoResponse);
      }
    });

    return buildResponse(fetchedPhotosByProviderKey);
  } catch (error) {
    ClientLogsService.logError(
      request.user_uuid || "unknown",
      "provider-photos.service.getProviderPhotos",
      "Error fetching provider photos",
      error instanceof Error ? error : new Error(String(error)),
      {
        errorType: "DependencyFailure",
        dependencyName: "ProviderPhotosAPI",
        functionName: "getProviderPhotos",
      },
    );

    return {
      success: false,
      error: "Failed to fetch provider photos",
    };
  }
}

export function mapProviderPhotosByProviderKey(
  photos: ProviderPhoto[] = [],
): Record<string, ProviderPhoto> {
  return photos.reduce<Record<string, ProviderPhoto>>((photoMap, photo) => {
    photoMap[buildProviderPhotoKey(photo)] = photo;
    return photoMap;
  }, {});
}
